import React from 'react';
import './PulseBadge.css';

export type PulseBadgeVariant = 'orange' | 'cyan' | 'green' | 'red' | 'purple';
export type PulseBadgeSize = 'xs' | 'sm' | 'md';

export interface PulseBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Color theme — default `orange` (matches PvP XP 2x badge). */
  variant?: PulseBadgeVariant;
  /** Padding/font-size scale. `xs` matches the home-tab badges; `md` matches the lives `×2`. */
  size?: PulseBadgeSize;
  /** Toggle the pulse animation (defaults to true). */
  pulse?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Reusable pill badge with a synchronized scale + glow pulse.
 *
 * The visual contract (gradient, font-weight, border-radius, pulse keyframe and
 * timing) is shared across the app — originally the PvP "XP 2x" tab badge.
 * Use this everywhere a small attention-grabbing chip is needed (boost active,
 * VIP perks, etc.). Forwards ref so it works as an OverlayTrigger child.
 */
const PulseBadge = React.forwardRef<HTMLSpanElement, PulseBadgeProps>(
  ({ variant = 'orange', size = 'xs', pulse = true, className = '', children, ...rest }, ref) => {
    const cls = [
      'pulse-badge',
      `pulse-badge--${variant}`,
      `pulse-badge--${size}`,
      pulse ? 'pulse-badge--animated' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <span ref={ref} className={cls} {...rest}>
        {children}
      </span>
    );
  },
);

PulseBadge.displayName = 'PulseBadge';

export default PulseBadge;
